/**
 * Public invite page: /invite/[token]
 * Installer sends link; homeowner signs up and is scoped to installer's org with role homeowner.
 */
import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { TermsAcceptanceLabel } from '@/lib/terms-acceptance';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

const signupSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

interface InviteBrand {
  company_name?: string;
  logo_url?: string | null;
  accent_color?: string | null;
  powered_by_wt?: boolean;
}

export default function Invite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [brand, setBrand] = useState<InviteBrand | null>(null);

  useEffect(() => {
    if (!token?.trim()) {
      setInviteValid(false);
      setInviteLoading(false);
      return;
    }
    (async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke<{
          email: string | null;
          valid: boolean;
          org_name?: string | null;
          brand_profile?: InviteBrand | null;
        }>('get-invite', {
          method: 'POST',
          body: { token: token.trim() },
        });
        if (fnError) {
          setInviteValid(false);
        } else {
          setInviteValid(data?.valid ?? false);
          setInviteEmail(data?.email ?? null);
          if (data?.email) setEmail(data.email);
          setOrgName(data?.org_name ?? null);
          setBrand(data?.brand_profile ?? null);
        }
      } catch {
        setInviteValid(false);
      } finally {
        setInviteLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token?.trim()) return;
    setError(null);
    setLoading(true);
    try {
      if (!agreeToTerms) {
        setError('You must agree to the Terms of Service and Privacy Policy to create an account.');
        setLoading(false);
        return;
      }
      const validation = signupSchema.safeParse({ email, password, confirmPassword });
      if (!validation.success) {
        setError(validation.error.errors[0].message);
        setLoading(false);
        return;
      }
      const redirectUrl = `${window.location.origin}/auth/callback?invite=${encodeURIComponent(token.trim())}`;
      const { error: signUpError, session } = await signUp(email, password, {
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        emailRedirectTo: redirectUrl,
      });
      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          setError('An account with this email already exists. Sign in and use the invite link again, or use a different email.');
        } else if (signUpError.message.toLowerCase().includes('pending approval')) {
          setError('Your account has been created and is pending approval. An administrator will approve your access soon.');
        } else {
          setError(signUpError.message);
        }
        setLoading(false);
        return;
      }
      if (session) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const res = await fetch(`${supabaseUrl}/functions/v1/accept-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ token: token.trim() }),
        });
        const result = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(result?.error ?? 'Failed to accept invite');
          setLoading(false);
          return;
        }
        navigate('/app', { replace: true });
        return;
      }
      setSignupSuccess(true);
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
        <Logo variant="fullTransparent" size="h-12" className="mb-6 opacity-90" />
        <p className="font-sans text-wt-text-secondary">Checking invite…</p>
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
        <Logo variant="fullTransparent" size="h-12" className="mb-6 opacity-90" />
        <p className="font-sans text-wt-text-primary text-center mb-6">
          This invite is invalid or has expired.
        </p>
        <Button asChild variant="outline" className="border-wt-border-default text-wt-text-secondary">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </div>
    );
  }

  if (signupSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
        <Logo variant="fullTransparent" size="h-12" className="mb-6 opacity-90" />
        <p className="font-sans text-wt-text-primary text-center mb-6">
          Check your email to confirm your account. Click the link we sent you; you'll then be signed in and linked to your installer's organization.
        </p>
        <Button asChild variant="outline" className="border-wt-border-default text-wt-text-secondary">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </div>
    );
  }

  const displayName = brand?.company_name || orgName || 'Watt Tower';
  const accentColor = brand?.accent_color || '#10B981';
  const accentHover = brand?.accent_color ? `${brand.accent_color}dd` : '#059669';
  const showPoweredBy = brand ? (brand.powered_by_wt ?? true) : false;

  return (
    <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-4">
            <Logo
              variant="fullTransparent"
              size="h-16"
              overrideUrl={brand?.logo_url}
              overrideAlt={brand?.logo_url ? displayName : undefined}
            />
          </Link>
          <h1 className="font-sans text-2xl font-bold text-wt-text-primary">Create your account</h1>
          <p className="font-sans text-wt-text-secondary mt-1 text-sm">
            You've been invited to {displayName}.
          </p>
        </div>

        <div className="rounded-lg border border-wt-border-default bg-wt-bg-secondary p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-sans text-wt-text-secondary">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-sans text-wt-text-secondary">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
              />
              <p className="font-sans text-xs text-wt-text-muted">At least 8 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-sans text-wt-text-secondary">Confirm password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
              />
            </div>
            <div className="flex items-start gap-3 rounded-md border border-wt-border-default bg-wt-bg-tertiary p-4">
              <Checkbox
                id="agreeToTerms"
                checked={agreeToTerms}
                onCheckedChange={(v) => setAgreeToTerms(v === true)}
                className="mt-0.5 border-wt-slate-blue data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Label
                htmlFor="agreeToTerms"
                className="font-sans text-sm font-normal leading-relaxed text-wt-text-secondary cursor-pointer"
              >
                <TermsAcceptanceLabel linkClassName="text-primary hover:underline" />
              </Label>
            </div>
            {error && (
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans">
                {error}
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-11 font-sans font-semibold text-wt-bg-primary"
              style={{ backgroundColor: accentColor }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = accentHover)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accentColor)}
              disabled={loading || !agreeToTerms}
            >
              {loading ? 'Please wait…' : 'Create account'}
            </Button>
          </form>
        </div>

        {showPoweredBy && (
          <p className="font-sans text-xs text-wt-text-muted text-center mt-4">
            Powered by Watt Tower
          </p>
        )}
        <p className="font-sans text-xs text-wt-text-muted text-center mt-2">
          <Link to="/" className="text-wt-text-secondary hover:text-wt-text-primary">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
